/**
 * Scatter Chart ECharts Adapter
 *
 * Transforms asset statistics data into ECharts scatter plot options.
 * Each asset is rendered as a point with x/y coordinates based on selected statistics.
 */

import type { EChartsCoreOption } from "echarts";
import type { ScatterChartAdapterProps, EChartAdapterResult } from "./types";

/**
 * Calculate a "nice" number (round number) for axis intervals
 */
function calculateNiceNumber(range: number, round: boolean): number {
  const exponent = Math.floor(Math.log10(range));
  const fraction = range / Math.pow(10, exponent);
  let niceFraction: number;

  if (round) {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 3) niceFraction = 2;
    else if (fraction < 7) niceFraction = 5;
    else niceFraction = 10;
  } else {
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;
  }

  return niceFraction * Math.pow(10, exponent);
}

/**
 * Calculate nice axis bounds with clean intervals
 */
function calculateNiceBounds(dataMin: number, dataMax: number, desiredTicks: number = 5): {
  min: number;
  max: number;
  interval: number;
} {
  const dataRange = dataMax - dataMin;
  
  // Add padding
  const paddingPercent = 0.1;
  const paddedRange = dataRange * (1 + paddingPercent * 2);
  
  // Calculate nice interval
  const rawInterval = paddedRange / desiredTicks;
  let interval = calculateNiceNumber(rawInterval, true);
  
  // Ensure interval is at least 0.01 (which becomes 1% when formatted)
  // This prevents half-percent intervals like 0.5%
  interval = Math.max(interval, 0.01);

  // Round min down and max up to nearest interval
  const niceMin = Math.floor(dataMin / interval) * interval;
  const niceMax = Math.ceil(dataMax / interval) * interval;

  return { min: niceMin, max: niceMax, interval };
}

/**
 * Format percentage values for axis labels
 */
function formatPercentage(value: number): string {
  const percentValue = value * 100;
  const rounded = Math.round(percentValue * 10) / 10;
  const formatted = Math.abs(rounded - Math.round(rounded)) < 0.001 
    ? Math.round(rounded).toString() 
    : rounded.toFixed(1);
  return `${formatted}%`;
}

/**
 * Create a diagonal line pattern for chart background
 * Returns a pattern object that can be used as backgroundColor in ECharts
 */
function createPatternBackground(isDarkMode: boolean): object {
  // Create a canvas pattern for diagonal lines
  const patternSize = 10;
  const canvas = document.createElement("canvas");
  canvas.width = patternSize;
  canvas.height = patternSize;
  const ctx = canvas.getContext("2d");
  
  if (ctx) {
    // Set transparent background
    ctx.clearRect(0, 0, patternSize, patternSize);
    
    // Draw diagonal line
    ctx.strokeStyle = isDarkMode ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, patternSize);
    ctx.lineTo(patternSize, 0);
    ctx.stroke();
  }
  
  return {
    image: canvas,
    repeat: "repeat",
  };
}

/**
 * Build ECharts option for scatter charts
 */
export function buildScatterChartOption(
  props: ScatterChartAdapterProps
): EChartAdapterResult {
  const {
    selectedAssets,
    assetDisplayNames,
    assetColors,
    assetStats,
    colorPalette,
    chartSettings,
    visualSettings,
    isDarkMode,
  } = props;

  const showTooltips = visualSettings.showTooltips ?? true;
  const showGridRows = visualSettings.showGridRows ?? true;
  const showGridColumns = visualSettings.showGridColumns ?? false;
  const showXAxis = visualSettings.showXAxis ?? true;
  const showYAxis = visualSettings.showYAxis ?? true;
  const showPatternBackground = visualSettings.showPatternBackground ?? false;

  const xLabel = chartSettings.xLabel || "X Value";
  const yLabel = chartSettings.yLabel || "Y Value";

  // Handle empty data
  if (!assetStats || selectedAssets.length === 0) {
    return {
      option: {},
      chartType: "scatter",
      hasData: false,
    };
  }

  // Theme colors
  const textColor = isDarkMode ? "#e2e8f0" : "#334155";
  const gridColor = isDarkMode ? "#475569" : "#e2e8f0";
  const axisLineColor = isDarkMode ? "#64748b" : "#94a3b8";

  // Build scatter series - one per asset for proper legend support
  const scatterSeries: Array<{
    type: string;
    name: string;
    data: Array<[number, number]>;
    itemStyle: { color: string };
    symbolSize: number;
    emphasis: { focus: string; itemStyle: { shadowBlur: number; shadowColor: string } };
  }> = [];

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  selectedAssets.forEach((assetId, index) => {
    const stats = assetStats[assetId];
    if (!stats) return;

    const x = stats.x || 0;
    const y = stats.y || 0;
    const name = assetDisplayNames[assetId] || stats.name || assetId;
    const color = assetColors[assetId] || colorPalette[index % colorPalette.length];

    // Track bounds
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);

    // Create a separate series for each asset
    scatterSeries.push({
      type: "scatter",
      name,
      data: [[x, y]],
      itemStyle: {
        color,
      },
      symbolSize: 12,
      emphasis: {
        focus: "self",
        itemStyle: {
          shadowBlur: 10,
          shadowColor: "rgba(0, 0, 0, 0.3)",
        },
      },
    });
  });

  // Handle case where all points have same coordinates
  if (minX === maxX) {
    minX = minX - 0.01;
    maxX = maxX + 0.01;
  }
  if (minY === maxY) {
    minY = minY - 0.01;
    maxY = maxY + 0.01;
  }

  // Calculate nice bounds with clean intervals for both axes
  const xBounds = calculateNiceBounds(minX, maxX, 4);
  const yBounds = calculateNiceBounds(minY, maxY, 4);

  // Start axes at 0 if all values are positive
  const xMin = minX >= 0 ? 0 : xBounds.min;
  const yMin = minY >= 0 ? 0 : yBounds.min;
  const xMax = xBounds.max;
  const yMax = yBounds.max;
  const xInterval = xBounds.interval;
  const yInterval = yBounds.interval;

  // Build the ECharts option
  const option: EChartsCoreOption = {
    backgroundColor: showPatternBackground ? createPatternBackground(isDarkMode) : "transparent",
    tooltip: showTooltips
      ? {
          trigger: "item",
          backgroundColor: isDarkMode ? "rgba(30, 41, 59, 0.95)" : "rgba(255, 255, 255, 0.95)",
          borderColor: isDarkMode ? "#475569" : "#e2e8f0",
          textStyle: {
            color: textColor,
          },
          formatter: (params: any) => {
            const [x, y] = params.value;
            // Use seriesName for the asset name (since each asset is a separate series)
            const assetName = params.seriesName || params.name || "Unknown";
            return `
              <div style="font-weight: 600; margin-bottom: 8px;">
                <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${params.color}; margin-right: 8px;"></span>
                ${assetName}
              </div>
              <div style="display: flex; flex-direction: column; gap: 4px;">
                <div>${xLabel}: <strong>${formatPercentage(x)}</strong></div>
                <div>${yLabel}: <strong>${formatPercentage(y)}</strong></div>
              </div>
            `;
          },
        }
      : undefined,
    legend: {
      show: false, // Use custom legend
    },
    grid: {
      left: showYAxis ? 80 : 10,
      right: 60,
      top: 40,
      bottom: showXAxis ? 60 : 30,
      containLabel: false,
      borderWidth: 0,
    },
    xAxis: {
      type: "value",
      name: showXAxis ? xLabel : "",
      nameLocation: "center",
      nameGap: 40,
      nameTextStyle: {
        color: textColor,
        fontSize: 12,
        fontFamily: "monospace",
      },
      min: xMin,
      max: xMax,
      interval: xInterval,
      show: true, // Always show for grid lines
      axisLine: {
        show: showXAxis,
        lineStyle: {
          color: axisLineColor,
        },
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        show: showXAxis,
        color: textColor,
        fontSize: 11,
        fontFamily: "monospace",
        formatter: (value: number) => formatPercentage(value),
        margin: 10,
      },
      splitLine: {
        show: showGridColumns,
        lineStyle: {
          color: gridColor,
          type: "solid",
          opacity: 0.3,
        },
      },
    },
    yAxis: {
      type: "value",
      name: showYAxis ? yLabel : "",
      nameLocation: "center",
      nameGap: 50,
      nameTextStyle: {
        color: textColor,
        fontSize: 12,
        fontFamily: "monospace",
      },
      min: yMin,
      max: yMax,
      interval: yInterval,
      show: true, // Always show for grid lines
      axisLine: {
        show: showYAxis,
        lineStyle: {
          color: axisLineColor,
        },
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        show: showYAxis,
        color: textColor,
        fontSize: 11,
        fontFamily: "monospace",
        formatter: (value: number) => formatPercentage(value),
        margin: 15,
      },
      splitLine: {
        show: showGridRows,
        lineStyle: {
          color: gridColor,
          type: "solid",
          opacity: 0.3,
        },
      },
    },
    series: scatterSeries,
    color: colorPalette,
  };

  return {
    option,
    chartType: "scatter",
    hasData: scatterSeries.length > 0,
  };
}
